import { useCallback } from 'react'
import { useLocalStorage } from './useLocalStorage'

const SAFE_STATUS_STORAGE_KEY = 'MSIGUI_safeStatus'

interface SafeStatusStorage {
  blocked: string[] // Array of safe IDs (chainId-address)
  deleted: string[] // Array of safe IDs (chainId-address)
}

export function useSafeStatus() {
  const [safeStatus, setSafeStatus] = useLocalStorage<SafeStatusStorage>(
    SAFE_STATUS_STORAGE_KEY,
    { blocked: [], deleted: [] }
  )

  const isBlocked = useCallback(
    (safeId: string): boolean => {
      return safeStatus.blocked.includes(safeId)
    },
    [safeStatus.blocked]
  )

  const isDeleted = useCallback(
    (safeId: string): boolean => {
      return safeStatus.deleted.includes(safeId)
    },
    [safeStatus.deleted]
  )

  const blockSafe = useCallback(
    (safeId: string) => {
      setSafeStatus((prev) => {
        if (prev.blocked.includes(safeId)) {
          return prev
        }
        return {
          ...prev,
          blocked: [...prev.blocked, safeId],
        }
      })
    },
    [setSafeStatus]
  )

  const unblockSafe = useCallback(
    (safeId: string) => {
      setSafeStatus((prev) => ({
        ...prev,
        blocked: prev.blocked.filter((id) => id !== safeId),
      }))
    },
    [setSafeStatus]
  )

  const deleteSafe = useCallback(
    (safeId: string) => {
      setSafeStatus((prev) => {
        if (prev.deleted.includes(safeId)) {
          return prev
        }
        return {
          ...prev,
          deleted: [...prev.deleted, safeId],
        }
      })
    },
    [setSafeStatus]
  )

  const restoreSafe = useCallback(
    (safeId: string) => {
      setSafeStatus((prev) => ({
        ...prev,
        deleted: prev.deleted.filter((id) => id !== safeId),
      }))
    },
    [setSafeStatus]
  )

  const filterDeleted = useCallback(
    <T extends { id: string }>(items: T[]): T[] => {
      return items.filter((item) => !isDeleted(item.id))
    },
    [isDeleted]
  )

  return {
    isBlocked,
    isDeleted,
    blockSafe,
    unblockSafe,
    deleteSafe,
    restoreSafe,
    filterDeleted,
  }
}
